import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConnectingToDappDisplay } from './ConnectingToDappDisplay';
import { Plus } from 'lucide-react';
import { useEffect, useId, useState } from 'react';

type ConnectDappSectionProps = {
  walletConnectUri: string;
  onWalletConnectUriChange: (value: string) => void;
  isConnecting: boolean;
  hasConnections: boolean;
  onConnect: () => void;
  onPendingDappConnectionCancelled: () => void;
  connectionError: string | null;
};

/**
 * Input and button to paste a dapp's WalletConnect URI and connect to it
 */
export function ConnectDappSection({
  walletConnectUri,
  onWalletConnectUriChange,
  isConnecting,
  hasConnections,
  onConnect,
  onPendingDappConnectionCancelled,
  connectionError,
}: ConnectDappSectionProps): React.ReactElement {
  const [showConnectAnotherDappButton, setShowConnectAnotherDappButton] =
    useState(hasConnections);
  const walletConnectInputId = useId();

  useEffect(() => {
    // Keep button visibility in sync when first connection appears/disappears.
    setShowConnectAnotherDappButton(hasConnections);
  }, [hasConnections]);

  // Case: there are connected dapps, show the connect another dapp button
  if (hasConnections && showConnectAnotherDappButton) {
    return (
      <Button
        onClick={() => setShowConnectAnotherDappButton(false)}
        className="h-[59px] w-full rounded-[62px] bg-neutral-800 text-[16px] font-medium leading-[24px] text-white hover:bg-neutral-700 mt-[10px] mb-[20px]"
      >
        <Plus className="mr-2 h-[24px] w-[24px]" />
        Connect another Dapp
      </Button>
    );
  }
  return (
    <div className="w-full max-w-[479px] rounded-[20px] border border-zinc-200 bg-zinc-100 shadow-[0px_4px_6px_-3px_rgba(0,0,0,0.1)] mt-[10px] mb-[20px]">
      <div className="flex items-center justify-center p-[10px]">
        <p className="text-[14px] font-medium leading-[20px] text-zinc-500">
          Connect a dapp
        </p>
      </div>
      <div className="rounded-[20px] border border-zinc-200 bg-zinc-50 px-[20px] py-[30px]">
        {isConnecting ? (
          <ConnectingToDappDisplay
            onPendingDappConnectionCancelled={onPendingDappConnectionCancelled}
          />
        ) : (
          <div className="flex flex-col gap-[19px]">
            <div className="flex flex-col gap-[5px]">
              <p className="text-[20px] font-medium leading-[20px] text-zinc-700">
                Paste in the dapp&apos;s WalletConnect Link
              </p>
              <p className="text-[12px] font-normal leading-[20px] text-zinc-500">
                Copy the link from the dapp&apos;s WalletConnect pop up
              </p>
            </div>

            <Label htmlFor={walletConnectInputId} className="sr-only">
              WalletConnect link
            </Label>
            <Input
              id={walletConnectInputId}
              value={walletConnectUri}
              onChange={(event) => onWalletConnectUriChange(event.target.value)}
              placeholder="WalletConnect Link"
              className="h-[52px] w-full rounded-[10px] border border-zinc-400 bg-white px-[20px] text-[16px] font-normal text-zinc-700 outline-none placeholder:text-zinc-500"
            />

            <Button
              onClick={onConnect}
              disabled={isConnecting}
              className="h-[40px] w-full rounded-[62px] bg-neutral-800 text-[16px] font-medium leading-[24px] text-white hover:bg-neutral-700"
            >
              Continue
            </Button>

            {connectionError && (
              <p className="text-sm text-destructive">{connectionError}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
